/**
 * Canonical HubHive CHAT app store URLs — the single source of truth for any
 * "get the chat app" surface (the `/loop` download page and the `/m/{code}`
 * Invite Link landing, ADR-0059), mirroring `not-found/store-links.ts` for the
 * main app — and the Loop build each host links to, for the association files
 * and the link that opens Loop on a Mac (ADR-0158).
 */

export const CHAT_PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.hubhive.chat'

export const CHAT_APP_STORE_URL =
  'https://apps.apple.com/us/app/hubhive-loop/id6785090238'

/**
 * An invite code as the server defines it (`domain/chat/conversation.go`):
 * exactly 8 alphanumerics for a personal Invite Link (ADR-0059), exactly 10
 * for a group Join Link (ADR-0076) — "anything that is neither length is not a
 * code at all". Only a string passing this is ever reflected into a URL.
 */
const INVITE_CODE = /^(?:[A-Za-z0-9]{8}|[A-Za-z0-9]{10})$/

export function isInviteCode(code: string | null | undefined): code is string {
  return typeof code === 'string' && INVITE_CODE.test(code)
}

/**
 * The Loop Play listing, carrying an invite code as the Play **install
 * referrer**. Play hands the string back to our own app after install (Install
 * Referrer API), so a scan made without the app still becomes its conversation
 * — a round trip, not a disclosure. The App Store has no equivalent, so iOS
 * never gets a code: sending one there would hand a bearer grant to a third
 * party and buy nothing.
 *
 * A query string, not a bare code, so later params (campaign, source) can
 * join it without breaking the reader. The app parses it with
 * splitQueryString; the key `m` is a pinned literal on both sides
 * (`deferred_invite_consumer.dart`). Anything that is not a code gets the
 * plain listing — they may well want the app, but nothing is echoed.
 */
export function playStoreUrlWithInviteReferrer(code: string | null | undefined): string {
  if (!isInviteCode(code)) return CHAT_PLAY_STORE_URL
  return `${CHAT_PLAY_STORE_URL}&referrer=${encodeURIComponent(`m=${code}`)}`
}

/**
 * The Loop build that owns each host. A dev host must never hand a link to
 * prod Loop, nor prod to dev (ADR-0059), so this map scopes the association
 * files' Loop entry and Loop's URL scheme alike. Loop's bundle id is the same
 * on every platform, and on a Mac it is also Loop's URL scheme (ADR-0158).
 * Loop has no prerelease flavor, so that host has no Loop at all.
 */
const LOOP_BUNDLE_BY_HOST: Record<string, string> = {
  'dev.hubhive.com': 'com.hubhive.chat.dev',
  'hubhive.com': 'com.hubhive.chat',
  'www.hubhive.com': 'com.hubhive.chat',
  localhost: 'com.hubhive.chat.local',
}
// Unknown / missing host → prod Loop (hubhive.com is the canonical public host).
const DEFAULT_LOOP_BUNDLE = 'com.hubhive.chat'
const PRERELEASE_HOST = 'prerelease.hubhive.com'

/** A request `Host` header without its port, lowercased. */
function hostnameOf(rawHost: string | null | undefined): string {
  return (rawHost ?? '').split(':')[0].trim().toLowerCase()
}

/** The Loop bundle id for a request host, or null on a host with no Loop. */
export function loopBundleForHost(rawHost: string | null | undefined): string | null {
  const host = hostnameOf(rawHost)
  if (host === PRERELEASE_HOST) return null
  return LOOP_BUNDLE_BY_HOST[host] ?? DEFAULT_LOOP_BUNDLE
}

/**
 * The link that opens an Invite or Join Link in Loop on a Mac through Loop's
 * own URL scheme (ADR-0158): `{bundle id}://{host}/m/{code}`. On a Mac only a
 * click inside Safari hands the `https` Universal Link to Loop, so the desktop
 * `/m/{code}` page offers this instead.
 *
 * The host stays in the URL so that its path is the Invite Link's own and the
 * app's parser takes it unchanged; without one, `m` would parse as the host.
 * Only a code passing the server's rule is reflected, and a host that is not a
 * plain hostname falls back to the canonical one rather than being echoed.
 * Null when there is no code, or no Loop for the host.
 */
export function openInLoopUrl(
  rawHost: string | null | undefined,
  code: string | null | undefined,
): string | null {
  const bundle = loopBundleForHost(rawHost)
  if (!bundle || !isInviteCode(code)) return null
  const host = hostnameOf(rawHost)
  return `${bundle}://${/^[a-z0-9.-]+$/.test(host) ? host : 'hubhive.com'}/m/${code}`
}
